import { Accordion } from 'react-bootstrap';

const defaultGetGroupId = (group, index) => group?.id ?? group?.label ?? `group-${index}`;
const defaultGetSectionId = (section, index) =>
  section?.id ?? section?.label ?? `section-${index}`;
const defaultGetItemKey = (item, index) => item?.id ?? item?.value ?? index;

export default function BaseMultiDropdown({
  groups = [],
  loading = false,
  loadingMessage = 'Loading...',
  errorMessage = '',
  emptyMessage = 'No options available.',
  containerClassName = 'd-flex flex-column gap-2 bg-primary rounded-3 border border-light border-opacity-25 p-2 h-100 overflow-auto',
  groupClassName = 'd-flex flex-column gap-2',
  groupHeaderClassName = 'd-flex align-items-center px-1',
  groupHeaderTextClassName = 'fw-semibold lh-sm',
  accordionClassName = 'w-100 acc-theme-primary',
  sectionClassName = 'border border-light border-opacity-25 rounded-2 overflow-hidden bg-transparent',
  sectionSpacingClassName = 'mb-2',
  sectionBodyClassName = 'bg-transparent p-0',
  emptyStateClassName = 'text-secondary',
  showGroupHeaders = true,
  sectionsCollapsible = true,
  getGroupId = defaultGetGroupId,
  getSectionId = defaultGetSectionId,
  getItemKey = defaultGetItemKey,
  getDefaultActiveKeys = (group) =>
    (group?.sections || []).filter((section) => (section?.selectedCount || 0) > 0).map((section) => section.id),
  renderGroupHeader,
  renderSectionHeader,
  renderSectionContent,
  renderItem,
}) {
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-secondary">
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        {loadingMessage}
      </div>
    );
  }

  if (errorMessage) return <div className="text-danger">{errorMessage}</div>;

  if (!groups.length) return <div className={emptyStateClassName}>{emptyMessage}</div>;

  return (
    <div className={containerClassName}>
      {groups.map((group, groupIndex) => {
        const groupId = getGroupId(group, groupIndex);
        const sections = group?.sections || [];
        const defaultActiveKeys = getDefaultActiveKeys(group) || [];

        return (
          <div key={groupId} className={groupClassName}>
            {showGroupHeaders &&
              (renderGroupHeader ? (
                renderGroupHeader(group, groupIndex)
              ) : (
                <div className={groupHeaderClassName}>
                  <div className={groupHeaderTextClassName}>{group?.label || groupId}</div>
                </div>
              ))}

            {sectionsCollapsible ? (
              <Accordion
                alwaysOpen
                defaultActiveKey={defaultActiveKeys}
                className={accordionClassName}
              >
                {sections.map((section, sectionIndex) => {
                  const sectionId = getSectionId(section, sectionIndex);
                  const sectionClassNames = [
                    sectionClassName,
                    sectionIndex < sections.length - 1 ? sectionSpacingClassName : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <Accordion.Item eventKey={sectionId} key={sectionId} className={sectionClassNames}>
                      <Accordion.Header>
                        {renderSectionHeader ? (
                          renderSectionHeader(section, group, sectionIndex)
                        ) : (
                          <div className="d-flex w-100 align-items-center justify-content-between gap-2 pe-2">
                            <span className="text-break">{section?.label || sectionId}</span>
                            <span className="d-inline-flex align-items-center justify-content-center px-2 py-0 rounded-pill border border-light border-opacity-25 bg-dark bg-opacity-25 small text-nowrap">
                              {section?.countLabel ??
                                `${section?.selectedCount || 0} ${section?.countLabelSuffix || 'selected'}`}
                            </span>
                          </div>
                        )}
                      </Accordion.Header>
                      <Accordion.Body className={sectionBodyClassName}>
                        {renderSectionContent ? (
                          renderSectionContent(section, group, sectionIndex)
                        ) : (
                          <div className="d-flex flex-column">
                            {(section?.items || []).map((item, itemIndex) =>
                              renderItem ? (
                                renderItem(item, {
                                  group,
                                  section,
                                  groupIndex,
                                  sectionIndex,
                                  itemIndex,
                                  itemKey: getItemKey(item, itemIndex),
                                })
                              ) : null,
                            )}
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            ) : (
              <div className={accordionClassName}>
                {sections.map((section, sectionIndex) => {
                  const sectionId = getSectionId(section, sectionIndex);
                  const sectionClassNames = [
                    sectionClassName,
                    sectionIndex < sections.length - 1 ? sectionSpacingClassName : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div key={sectionId} className={sectionClassNames}>
                      <div className="px-3 py-2">
                        {renderSectionHeader ? (
                          renderSectionHeader(section, group, sectionIndex)
                        ) : (
                          <div className="d-flex w-100 align-items-center justify-content-between gap-2">
                            <span className="text-break">{section?.label || sectionId}</span>
                            <span className="d-inline-flex align-items-center justify-content-center px-2 py-0 rounded-pill border border-light border-opacity-25 bg-dark bg-opacity-25 small text-nowrap">
                              {section?.countLabel ??
                                `${section?.selectedCount || 0} ${section?.countLabelSuffix || 'selected'}`}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className={sectionBodyClassName}>
                        {renderSectionContent ? (
                          renderSectionContent(section, group, sectionIndex)
                        ) : (
                          <div className="d-flex flex-column">
                            {(section?.items || []).map((item, itemIndex) =>
                              renderItem ? (
                                renderItem(item, {
                                  group,
                                  section,
                                  groupIndex,
                                  sectionIndex,
                                  itemIndex,
                                  itemKey: getItemKey(item, itemIndex),
                                })
                              ) : null,
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
